/** Alex tab local app-state module: a self-contained local reducer + context providing
 *  useAppDispatch/useMessagingState/useUserState/useAuthState over the messaging slice plus shell-derived user state. */

import { createContext, useReducer, useSyncExternalStore, type Dispatch, type ReactNode } from 'react';
import { REDUCER_ACTION_TYPES } from '@federa/const/reducer-actions';
import { resolveViewerSubject } from '@federa/utils/auth/viewer-subject';
import { readStoredGuestSessionToken } from '@federa/utils/auth/guest-session-token-storage';
import { readGuestSessionTokenClaimsUnverified } from '@federa/utils/security/guest-session-token';
import { createContextHook } from '@federa/components/hooks/createContextHook';
import { subscribeToShell } from '@federa/components/common/federa-shell-reader';
import { globalKey } from '@federa/const/global-namespace';
import { parseUserId } from '@federa/utils/strings/parse';
import type { AppAction, AuthState, MessagingState, UserState } from '@federa/types/react-app-state';

const INITIAL_MESSAGING: MessagingState = {
	chatRoomList: [],
	currentMessages: [],
	currentParticipants: [],
	agentStatus: null,
};

/** Reduce the messaging subset of AppAction; other actions fall through unchanged, so an if-chain expresses the partial coverage. */
function messagingReducer(state: MessagingState, action: AppAction): MessagingState {
	if (action.type === REDUCER_ACTION_TYPES.MESSAGING_UPDATE) return { ...state, ...action.payload };
	if (action.type === REDUCER_ACTION_TYPES.MESSAGING_SET_MESSAGES) return { ...state, currentMessages: action.payload };
	if (action.type === REDUCER_ACTION_TYPES.MESSAGING_SET_ROOMS) return { ...state, chatRoomList: action.payload };
	if (action.type === REDUCER_ACTION_TYPES.MESSAGING_ADD_ROOM) return { ...state, chatRoomList: [...state.chatRoomList, action.payload] };
	if (action.type === REDUCER_ACTION_TYPES.MESSAGING_SET_AGENT_STATUS) return { ...state, agentStatus: action.payload };
	return state;
}

interface AlexStateValue {
	messaging: MessagingState;
	dispatch: Dispatch<AppAction>;
}

const AlexStateContext = createContext<AlexStateValue | null>(null);

export function AlexStateProvider({ children }: { children: ReactNode }) {
	const [messaging, dispatch] = useReducer(messagingReducer, INITIAL_MESSAGING);
	return <AlexStateContext.Provider value={{ messaging, dispatch }}>{children}</AlexStateContext.Provider>;
}

const useAlexState = createContextHook(AlexStateContext, 'useAlexState', 'AlexStateProvider');

export function useAppDispatch(): Dispatch<AppAction> {
	return useAlexState().dispatch;
}

export function useMessagingState(): MessagingState {
	return useAlexState().messaging;
}

interface ShellUserSnapshot {
	userId?: string;
	rawUserId?: string;
	name?: string;
	username?: string;
	role?: string;
	isLoggedIn?: boolean;
}

interface ShellSnapshot {
	user?: ShellUserSnapshot;
	auth?: { isAuthInitialized?: boolean };
}

// Brand-key read without the Window-widening double cast (rationale: components/common/federa-shell-reader.ts).
function readShell(): ShellSnapshot | undefined {
	return Reflect.get(window, globalKey('Shell')) as ShellSnapshot | undefined;
}

// getSnapshot runs on every render, so the base64 decode is memoized on the token string it came from.
let guestSubjectCache: { token: string | null; gid: string | null } = { token: null, gid: null };

function readGuestCredentialSubject(): string | null {
	const token = readStoredGuestSessionToken();
	if (!token) return null;
	if (token !== guestSubjectCache.token) {
		guestSubjectCache = { token, gid: readGuestSessionTokenClaimsUnverified(token)?.gid ?? null };
	}
	return guestSubjectCache.gid;
}

/**
 * The viewer's identity for OWNERSHIP decisions -- which messages are mine, and which echoed frame settles an optimistic
 * one. A signed-in user answers with the shell's `userId`; a GUEST answers with their guest id.
 *
 * The guest arm is not a widening of the login gate, it is the whole point: a guest IS a subject here. The DO stamps the
 * upgrade-verified guest id onto every message it persists, so `computeIsOwn` compares against it and
 * `deduplicateMessages` matches the optimistic row on it. Returning '' for a guest broke both at once -- the id branch
 * of `computeIsOwn` is guarded on a truthy id, so it fell through to the NAME branch, where a stored "Guest" never
 * matches the `PLACEHOLDER_USERNAME` "User" and a guest's own turns rendered as someone else's; and the optimistic row
 * carried '' as its sender while the echo carried the real id, so the two never matched and every sent message doubled
 * until a reload replaced the pair with the one persisted row.
 *
 * The guest id comes from the CREDENTIAL, not the shell. Both name a guest, but they are separate stores that
 * legitimately diverge -- the explore surface mints its own identity into the shell slot while the chats token stays
 * signed for whichever id created the conversation. The DO stamps messages with the TOKEN's subject, so ownership has
 * to be decided by the same value or a guest with a divergent pair reads their own turns as someone else's. Measured in
 * a real returning-guest browser: chats token `guest_543f...`, shell `guest_589dda...`, plus a third legacy `guest_f5e0...`.
 *
 * `isLoggedIn` DOES NOT discriminate guest from authed -- that same browser reported `isLoggedIn: true` while holding a
 * guest id in every slot. Gating the credential behind it silently restored the shell as the answer for exactly the
 * guests this exists to serve. The ID SHAPE is the discriminator: a subject WITHOUT `GUEST_ID_PREFIX` is a real account
 * and wins outright, and everything else resolves to the credential.
 *
 * Order matters in the other direction too: an authenticated viewer can still carry a stale chats guest token from a
 * signed-out session, so the account id is taken FIRST and the credential is never consulted for them.
 *
 * NAMING, for this whole `snapshot*` family: `components/common/federa-shell-reader.ts` EXPORTS `getShellUserId` and
 * `getShellRole` on a DIFFERENT contract -- `string | null`, and its userId answers only for a logged-in account, so a
 * guest reads as null. These are `useSyncExternalStore` snapshot getters and must never return null, and this one is
 * guest-aware by design. Sharing the `getShell*` spelling gave one name two answers on exactly the axis that broke
 * ownership above, so the whole family carries the snapshot prefix instead.
 */
function snapshotShellUserId(): string {
	const user = readShell()?.user;
	return resolveViewerSubject({
		shellUserId: user?.userId,
		credentialSubject: readGuestCredentialSubject(),
		rawUserId: user?.rawUserId,
	});
}
function snapshotShellRawUserId(): string {
	return readShell()?.user?.rawUserId ?? '';
}
function snapshotShellUsername(): string {
	return readShell()?.user?.username ?? '';
}
function snapshotShellName(): string {
	return readShell()?.user?.name ?? '';
}
function snapshotShellRole(): string {
	return readShell()?.user?.role ?? '';
}
function snapshotShellAuthInitialized(): boolean {
	return readShell()?.auth?.isAuthInitialized ?? false;
}

/** Shell-derived UserState. Only the identity fields the assistant path reads carry live values; the financial fields of the shared UserState shape are inert defaults here. */
export function useUserState(): UserState {
	const userId = useSyncExternalStore(subscribeToShell, snapshotShellUserId);
	const rawUserId = useSyncExternalStore(subscribeToShell, snapshotShellRawUserId);
	const username = useSyncExternalStore(subscribeToShell, snapshotShellUsername);
	const name = useSyncExternalStore(subscribeToShell, snapshotShellName);
	const role = useSyncExternalStore(subscribeToShell, snapshotShellRole);
	return {
		userId: parseUserId(userId),
		rawUserId,
		name,
		username,
		role,
		phoneNumberOtp: '',
		bankAccount: '',
		wireReferenceCode: '',
		currentBalance: 0,
		buyingPower: '',
	};
}

/** Shell-derived AuthState. Only isAuthInitialized carries a live value. */
export function useAuthState(): AuthState {
	const isAuthInitialized = useSyncExternalStore(subscribeToShell, snapshotShellAuthInitialized);
	return {
		team: '',
		isAuthInitialized,
	};
}
